/**
 * Relewise: Search label localization
 *
 * Mutates `window.relewiseUISearchOptions.localization` in place, and never
 * calls `useSearch()` again — that would replace the app's whole config.
 */

import { relewiseTags } from '@/shared/constants/constants';
import { deepQuerySelectorAll } from '@/shared/tracking/shadow-dom';

const isUsableLabel = (value: unknown): value is string =>
  typeof value === 'string' && value.trim() !== '';

type RequestUpdatable = HTMLElement & { requestUpdate: () => void };

const isRelewiseComponentWithRequestUpdate = (
  el: HTMLElement
): el is RequestUpdatable => {
  if (!el.tagName.toLowerCase().startsWith(relewiseTags.TAG_PREFIX)) {
    return false;
  }
  return 'requestUpdate' in el && typeof el.requestUpdate === 'function';
};

// Mutating the options object does not re-render Relewise's Lit components
// that already rendered before this ran, so each Relewise element already in
// the DOM (light or shadow) is nudged to redraw with the values just written.
function redrawRenderedComponents() {
  const roots = document.querySelectorAll<HTMLElement>(
    `${relewiseTags.OVERLAY_TAG}, ${relewiseTags.SEARCH_TAG}`
  );

  roots.forEach((root) => {
    [root, ...deepQuerySelectorAll(root, '*')]
      .filter(isRelewiseComponentWithRequestUpdate)
      .forEach((el) => el.requestUpdate());
  });
}

function applyTranslations(translations: RelewiseTranslations) {
  const options = window.relewiseUISearchOptions;
  if (!options) return;

  const localization = (options.localization ??= {});
  const searchBar = (localization.searchBar ??= {});
  const overlay = (searchBar.overlay ??= {});
  const title = (overlay.title ??= {});
  const searchResults = (localization.searchResults ??= {});
  const sortingButton = (localization.sortingButton ??= {});
  const loadMoreButton = (localization.loadMoreButton ??= {});
  const facets = (localization.facets ??= {});

  const setters: [keyof RelewiseTranslations, (value: string) => void][] = [
    ['products', (value) => (title.products = value)],
    ['categories', (value) => (title.productCategories = value)],
    ['showAllResults', (value) => (searchResults.showAllResults = value)],
    ['noResults', (value) => (searchResults.noResults = value)],
    ['placeholder', (value) => (searchBar.placeholder = value)],
    ['searchButton', (value) => (searchBar.search = value)],
    ['sortBy', (value) => (sortingButton.sortBy = value)],
    ['sortRelevance', (value) => (sortingButton.relevance = value)],
    [
      'sortPriceAscending',
      (value) => (sortingButton.salesPriceAscending = value)
    ],
    [
      'sortPriceDescending',
      (value) => (sortingButton.salesPriceDescending = value)
    ],
    [
      'sortAlphabeticalAscending',
      (value) => (sortingButton.alphabeticalAscending = value)
    ],
    [
      'sortAlphabeticalDescending',
      (value) => (sortingButton.alphabeticalDescending = value)
    ],
    ['loadMore', (value) => (loadMoreButton.loadMore = value)],
    ['loadMoreShowing', (value) => (loadMoreButton.showing = value)],
    ['loadMoreOutOf', (value) => (loadMoreButton.outOf = value)],
    ['loadMoreProducts', (value) => (loadMoreButton.products = value)],
    ['showMore', (value) => (facets.showMore = value)],
    ['showLess', (value) => (facets.showLess = value)],
    // The app ships this label as facets.toggle, but the component reads facets.filter.
    ['filterButton', (value) => (facets.filter = value)]
  ];

  // Weblate can leave a market's translation blank, and an empty overlay
  // heading title breaks Relewise's arrow-key navigation by landing on an
  // invisible row, so a blank value must never overwrite Relewise's own
  // fallback string. The keys come from theme.liquid, which the type system
  // cannot check, so a missing key is skipped the same way instead of
  // throwing and dropping every label.
  setters.forEach(([key, set]) => {
    const value = translations[key];
    if (isUsableLabel(value)) set(value);
  });

  redrawRenderedComponents();
}

export function initRelewiseLocalization() {
  const translations = window.RelewiseConfig?.translations;
  if (!translations) return;

  customElements
    .whenDefined(relewiseTags.OVERLAY_TAG)
    .then(() => applyTranslations(translations))
    .catch(() => {});
}
